import { useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Inactivity timeout: 15 minutes
const INACTIVITY_TIMEOUT = 15 * 60 * 1000;
// Warning timeout: 13 minutes (2 minutes before logout)
const WARNING_TIMEOUT = 13 * 60 * 1000;

/**
 * Hook for automatic logout after inactivity
 * Required for fengselsvesenet (prison) security
 *
 * - Logs out users after 15 minutes of inactivity
 * - Shows warning after 13 minutes
 * - Does NOT apply to superadmin users
 */
export function useInactivityLogout() {
  const { profile, signOut } = useAuth();

  useEffect(() => {
    // Skip inactivity logout for superadmin
    if (profile?.role === 'superadmin') {
      return;
    }

    if (!profile) {
      return;
    }

    let inactivityTimer: NodeJS.Timeout;
    let warningTimer: NodeJS.Timeout;
    let hasShownWarning = false;

    const logout = async () => {
      try {
        await signOut();
        alert('Du har blitt logget ut på grunn av inaktivitet.');
        window.location.href = '/login';
      } catch (error) {
        console.error('Error during inactivity logout:', error);
        // Force logout anyway
        sessionStorage.clear();
        window.location.href = '/login';
      }
    };

    const showWarning = () => {
      if (hasShownWarning) return;
      hasShownWarning = true;

      const shouldStay = confirm(
        'Du har vært inaktiv i 13 minutter.\n\n' +
        'Du vil bli logget ut automatisk om 2 minutter.\n\n' +
        'Klikk OK for å forbli innlogget.'
      );

      if (shouldStay) {
        hasShownWarning = false;
        resetTimers();
      } else {
        // User clicked Cancel or closed dialog - log out immediately
        logout();
      }
    };

    const resetTimers = () => {
      // Clear existing timers
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);

      // Set warning timer (13 minutes)
      warningTimer = setTimeout(showWarning, WARNING_TIMEOUT);

      // Set logout timer (15 minutes)
      inactivityTimer = setTimeout(logout, INACTIVITY_TIMEOUT);
    };

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, resetTimers, { passive: true });
    });

    // Start timers
    resetTimers();

    // Cleanup
    return () => {
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetTimers);
      });
    };
  }, [profile, signOut]);
}
